// Custom Graph（Grammar of Graphics準拠）の型定義
// 散布図のみをサポートする最小実装

import { BaseGraphConfig } from './graph-builder.types';
import { ColumnId, DatasetId } from './id.types';

// =============================================================================
// Field Type
// =============================================================================

/**
 * フィールドのデータ型
 * MIDASのMeasurementScaleから自動推論される
 */
export type FieldType =
  | 'numeric'      // 連続的な数値（interval + ratio）
  | 'categorical'  // カテゴリカルデータ（nominal + ordinal）
  | 'temporal';    // 時間データ（datetime, date）

// =============================================================================
// Channel Definition
// =============================================================================

/**
 * 美的要素のチャネル定義
 * 3つの指定方法をサポート：
 * 1. フィールド参照: ColumnId（シンプルなケース）
 * 2. 固定値: string | number
 * 3. 詳細設定: ChannelDef（将来拡張用）
 */
export type AesValue = ColumnId | string | number;

// =============================================================================
// Aesthetic Mapping
// =============================================================================

/**
 * Aesthetic Mapping
 * データ変数を視覚属性にマッピング
 * グローバルやレイヤー固有のマッピングではすべてオプショナル
 * 最終的にglobalとlayerをマージした時にx, yが必須になる
 */
export interface AesMapping {
  // 位置
  x?: AesValue;
  y?: AesValue;

  // 色
  color?: AesValue;    // メインの色チャネル
  fill?: AesValue;     // 塗りつぶし色（bar, areaなど）
  stroke?: AesValue;   // 枠線色

  // サイズと形状
  size?: AesValue;     // ポイントサイズ
  shape?: AesValue;    // 形状（カテゴリカル）

  // 透明度と線種
  alpha?: AesValue;    // 透明度（0-1）
  linetype?: AesValue; // 線の種類（solid, dashed, dotted）

  // グループ化専用（視覚的変化なし、statのグループ化のみに使用）
  group?: AesValue;
}

// =============================================================================
// Geom (Geometry)
// =============================================================================

/**
 * サポートするGeomタイプ
 */
export type GeomType = 'point' | 'line' | 'bar' | 'area' | 'tile';

/**
 * 線の種類
 */
export type LineType = 'solid' | 'dashed' | 'dotted';

/**
 * 形状の種類
 */
export type ShapeType =
  | 'circle'      // 円（デフォルト）
  | 'square'      // 四角
  | 'triangle'    // 三角形
  | 'diamond'     // ダイヤモンド
  | 'cross'       // 十字
  | 'plus';       // プラス記号

/**
 * Point Geom定義
 */
export interface PointGeomDef {
  type: 'point';
  defaults?: {
    color?: string;
    size?: number;      // 1-20
    alpha?: number;     // 0-1
  };
}

/**
 * Line Geom定義
 */
export interface LineGeomDef {
  type: 'line';
  defaults?: {
    color?: string;
    strokeWidth?: number;  // 1-5
    lineType?: LineType;   // solid/dashed/dotted
    alpha?: number;        // 0-1
  };
}

/**
 * Bar Geom定義
 */
export interface BarGeomDef {
  type: 'bar';
  defaults?: {
    color?: string;
    width?: number;  // 0.1-1.0
    alpha?: number;  // 0-1
  };
}

/**
 * Area Geom定義
 */
export interface AreaGeomDef {
  type: 'area';
  defaults?: {
    color?: string;
    alpha?: number;  // 0-1
  };
}

/**
 * Tile Geom定義
 * ヒートマップ用の矩形タイルを描画
 */
export interface TileGeomDef {
  type: 'tile';
  defaults?: {
    width?: number;       // タイル幅（0.1-2.0、デフォルト: 自動計算）
    height?: number;      // タイル高さ（0.1-2.0、デフォルト: 自動計算）
    alpha?: number;       // 透明度（0-1）
    stroke?: string;      // 枠線色
    strokeWidth?: number; // 枠線幅
  };
}

/**
 * Geom定義（union type）
 */
export type GeomDef = PointGeomDef | LineGeomDef | BarGeomDef | AreaGeomDef | TileGeomDef;

// =============================================================================
// Stat (Statistical Transformation)
// =============================================================================

/**
 * サポートするStatタイプ
 */
export type StatType = 'identity' | 'bin' | 'smooth' | 'count' | 'density' | 'summary';

/**
 * Stat用のaestheticチャンネル
 * groupBy, input, outputで指定可能な値
 */
export type StatAestheticChannel = 'x' | 'y' | 'color' | 'fill' | 'size' | 'alpha' | 'group';

/**
 * 平滑化手法
 */
export type SmoothMethod = 'lm' | 'loess' | 'gam';

/**
 * Stat共通のベースプロパティ
 * - groupBy: グループ化キー（省略時は自動推論: x + カテゴリカルなcolor/fill/shape/group）
 * - input: 集約対象のaesthetic（省略時はstat依存のデフォルト）
 * - output: 出力先aesthetic（省略時はinputと同じ）
 */
interface StatBaseProps {
  groupBy?: StatAestheticChannel[];  // グループ化キー
  input?: StatAestheticChannel;      // 集約対象
  output?: StatAestheticChannel;     // 出力先
}

/**
 * Identity Stat定義（変換なし）
 */
export interface IdentityStatDef extends StatBaseProps {
  type: 'identity';
}

/**
 * Bin Stat定義（ヒストグラム用のビニング）
 * - デフォルト input: x
 * - デフォルト output: y
 */
export interface BinStatDef extends StatBaseProps {
  type: 'bin';
  params?: {
    bins?: number;       // ビン数（5-100）
    binwidth?: number;   // ビン幅（指定した場合はbinsを無視）
    yScale?: 'count' | 'density';  // Y軸のスケール（count: 件数、density: 密度）
  };
}

/**
 * Smooth Stat定義（平滑化曲線）
 * - デフォルト input: y
 * - デフォルト output: y
 */
export interface SmoothStatDef extends StatBaseProps {
  type: 'smooth';
  params?: {
    method?: SmoothMethod;  // 平滑化手法（lm/loess/gam）
    se?: boolean;           // 信頼区間を表示するか
    span?: number;          // loessのスパン（0.1-1.0）
    level?: number;         // 信頼区間のレベル（0-1、デフォルト0.95）
  };
}

/**
 * Count Stat定義（カウント集計）
 * - 入力なし（カウントするだけ）
 * - デフォルト output: y
 * - デフォルト groupBy: x + カテゴリカルaesthetics
 */
export interface CountStatDef extends StatBaseProps {
  type: 'count';
}

/**
 * Density Stat定義（カーネル密度推定）
 * - デフォルト input: x
 * - デフォルト output: y
 */
export interface DensityStatDef extends StatBaseProps {
  type: 'density';
  params?: {
    bandwidth?: number;  // カーネル帯域幅（オプション、未指定時は自動計算）
    kernel?: 'gaussian'; // カーネル関数（現在はgaussianのみ）
    yScale?: 'density' | 'count';  // Y軸スケール（density: 確率密度、count: カウント数相当）
  };
}

/**
 * Summary集約関数
 * ggplot2のstat_summary()のfunパラメータに相当
 */
export type SummaryFunction = 'mean' | 'sum' | 'median' | 'min' | 'max' | 'count';

/**
 * Summary Stat定義（集約統計）
 * グループ化キーでデータをグループ化し、inputの値をfunで集約してoutputに出力
 * - デフォルト groupBy: x + カテゴリカルaesthetics（yがあればyも含む）
 * - デフォルト input: y（yがなければfill）
 * - デフォルト output: inputと同じ
 */
export interface SummaryStatDef extends StatBaseProps {
  type: 'summary';
  params?: {
    fun?: SummaryFunction;  // 集約関数（デフォルト: 'mean'）
    complete?: boolean;     // X×Yの全組み合わせを生成（欠損は0で補完）
  };
}

/**
 * Stat定義（union type）
 */
export type StatDef = IdentityStatDef | BinStatDef | SmoothStatDef | CountStatDef | DensityStatDef | SummaryStatDef;

// =============================================================================
// Position (Position Adjustment)
// =============================================================================

/**
 * サポートするPositionタイプ
 */
export type PositionType = 'identity' | 'stack' | 'dodge' | 'jitter' | 'fill';

/**
 * Identity Position定義（調整なし）
 */
export interface IdentityPositionDef {
  type: 'identity';
}

/**
 * Stack Position定義（積み上げ）
 */
export interface StackPositionDef {
  type: 'stack';
  // 将来の拡張用にparamsを追加する可能性がある
}

/**
 * Dodge Position定義（並列配置）
 */
export interface DodgePositionDef {
  type: 'dodge';
  params?: {
    width?: number;  // 0-1、デフォルト0.9
  };
}

/**
 * Jitter Position定義（ランダムオフセット）
 */
export interface JitterPositionDef {
  type: 'jitter';
  params?: {
    width?: number;   // 0-1、デフォルト0.4
    height?: number;  // 0-1、デフォルト0.4
  };
}

/**
 * Fill Position定義（割合表示、100%積み上げ）
 */
export interface FillPositionDef {
  type: 'fill';
  // 将来の拡張用にparamsを追加する可能性がある
}

/**
 * Position定義（union type）
 */
export type PositionDef = IdentityPositionDef | StackPositionDef | DodgePositionDef | JitterPositionDef | FillPositionDef;

// =============================================================================
// Layer
// =============================================================================

/**
 * レイヤー定義
 * Geom + Stat + Position + Aesの組み合わせ
 */
export interface LayerDef {
  // レイヤー名（UI表示用）
  name?: string;

  // レイヤー固有のaesthetic mapping
  aes?: AesMapping;

  // 幾何学要素
  geom: GeomDef;

  // 統計変換（オプション、デフォルト: identity）
  stat?: StatDef;

  // 位置調整（オプション、デフォルト: identity）
  position?: PositionDef;

  // 表示/非表示
  visible?: boolean;

  // 使用するY軸（primary: 左側、secondary: 右側、デフォルト: primary）
  yAxis?: 'primary' | 'secondary';
}

// =============================================================================
// Scales
// =============================================================================

/**
 * スケールタイプ
 */
export type ScaleType = 'linear' | 'log' | 'sqrt' | 'time' | 'categorical';

/**
 * 連続スケール設定（数値・時間軸用）
 */
export interface ContinuousScaleDef {
  // スケールタイプ（linear, log, sqrt, time）
  type?: ScaleType;

  // 軸タイトル
  title?: string;

  // 範囲設定
  domain?: {
    min?: number;
    max?: number;
  };

  // Tick設定
  ticks?: {
    count?: number;      // tick数（オプション）
    format?: string;     // フォーマット文字列（オプション）
  };
}

/**
 * カテゴリカルスケール設定（カテゴリ軸用）
 * ggplot2のscale_x_discrete()、scale_y_discrete()に相当
 */
export interface CategoricalScaleDef {
  type: 'categorical';

  // 軸タイトル
  title?: string;

  // limits: カテゴリの順序を明示的に指定
  // 指定された順序でカテゴリが表示される
  // 未指定の場合はデータの出現順（数値文字列の場合は自然順ソート）
  limits?: string[];

  // breaks: 実際に表示するカテゴリのサブセット
  // 指定された場合、このカテゴリのみが軸に表示される
  breaks?: string[];

  // labels: カテゴリのカスタム表示名
  // key: カテゴリ値、value: 表示名
  labels?: Record<string, string>;

  // drop: 未使用のカテゴリを軸から削除するか
  // デフォルト: true
  drop?: boolean;

  // expand: 軸の余白（パディング係数）
  // デフォルト: 0.05（両端に5%の余白）
  expand?: number;
}

/**
 * 軸スケール設定（連続またはカテゴリカル）
 */
export type AxisScaleDef = ContinuousScaleDef | CategoricalScaleDef;

/**
 * 凡例の位置
 */
export type LegendPosition = 'right' | 'left' | 'top' | 'bottom' | 'none';

/**
 * カラースケールタイプ
 */
export type ColorScaleType = 'categorical' | 'sequential' | 'diverging';

/**
 * カラースケール設定
 * 既存のパレットシステム（palette-utils.ts）を使用
 */
export interface ColorScaleDef {
  // パレットID（palette-utils.tsで定義されたもの）
  // 例: 'midas-default', 'tableau10', 'viridis', 'blues', 'rdbu'など
  paletteId?: string;

  // スケールタイプ（categorical: カテゴリ、sequential: 連続、diverging: 発散）
  // デフォルト: categorical
  scaleType?: ColorScaleType;

  // 連続スケール用のドメイン設定
  // 未指定の場合はデータから自動計算
  domain?: {
    min?: number;
    max?: number;
  };

  // 凡例の位置
  legendPosition?: LegendPosition;

  // 凡例のタイトル
  legendTitle?: string;

  // カテゴリカルスケール設定（カテゴリカルカラーマッピング用）
  // limits: カテゴリの順序を指定
  limits?: string[];

  // breaks: 表示するカテゴリのサブセット
  breaks?: string[];

  // labels: カテゴリのカスタム表示名
  labels?: Record<string, string>;
}

/**
 * サイズスケール設定
 */
export interface SizeScaleDef {
  // サイズ範囲（ピクセル）
  range?: {
    min?: number;  // 最小サイズ（デフォルト: 2）
    max?: number;  // 最大サイズ（デフォルト: 20）
  };

  // 凡例設定
  legendPosition?: LegendPosition;
  legendTitle?: string;
}

/**
 * 形状スケール設定
 */
export interface ShapeScaleDef {
  // 使用する形状の配列（指定しない場合はデフォルト順）
  shapes?: ShapeType[];

  // 凡例設定
  legendPosition?: LegendPosition;
  legendTitle?: string;
}

/**
 * 透明度スケール設定
 */
export interface AlphaScaleDef {
  // 透明度範囲（0-1）
  range?: {
    min?: number;  // 最小透明度（デフォルト: 0.2）
    max?: number;  // 最大透明度（デフォルト: 1.0）
  };

  // 凡例設定
  legendPosition?: LegendPosition;
  legendTitle?: string;
}

/**
 * 線種スケール設定
 */
export interface LinetypeScaleDef {
  // 使用する線種の配列（指定しない場合はデフォルト順）
  linetypes?: LineType[];

  // 凡例設定
  legendPosition?: LegendPosition;
  legendTitle?: string;
}

/**
 * Scales設定
 */
export interface ScalesDef {
  // 位置スケール
  x?: AxisScaleDef;
  y?: AxisScaleDef;
  y2?: AxisScaleDef;  // Secondary Y軸（右側）

  // 色スケール
  color?: ColorScaleDef;
  fill?: ColorScaleDef;
  stroke?: ColorScaleDef;

  // サイズと形状スケール
  size?: SizeScaleDef;
  shape?: ShapeScaleDef;

  // 透明度と線種スケール
  alpha?: AlphaScaleDef;
  linetype?: LinetypeScaleDef;
}

// =============================================================================
// Coordinate System
// =============================================================================

/**
 * 座標系タイプ
 * 注: polar座標系は複雑さとユースケースの少なさから削除されました
 */
export type CoordType = 'cartesian' | 'flip';

/**
 * Cartesian座標系（デカルト座標、デフォルト）
 */
export interface CartesianCoordDef {
  type: 'cartesian';
}

/**
 * Flip座標系（X軸とY軸を入れ替え）
 */
export interface FlipCoordDef {
  type: 'flip';
}

/**
 * Coord定義（union type）
 */
export type CoordDef = CartesianCoordDef | FlipCoordDef;

// =============================================================================
// Facets
// =============================================================================

/**
 * Facet Wrap: 1つの変数でファセット分割
 * ggplot2のfacet_wrap()に相当
 */
export interface FacetWrapDef {
  type: 'wrap';
  variable: ColumnId;  // 分割する変数（カテゴリカルまたは離散的な値を想定）
  ncol?: number;       // 列数（指定しない場合は自動計算）
  nrow?: number;       // 行数（指定しない場合は自動計算）
  complete?: boolean;  // 全パネルで共通のX×Y（または1D）組み合わせを使用して欠損を補完
}

/**
 * Facet Grid: 2つの変数で行・列にファセット分割
 * ggplot2のfacet_grid()に相当
 */
export interface FacetGridDef {
  type: 'grid';
  rows?: ColumnId;  // 行方向の分割変数（省略可能）
  cols?: ColumnId;  // 列方向の分割変数（省略可能）
  // 注: rowsとcolsの少なくとも1つは必須
  complete?: boolean;  // 全パネルで共通のX×Y（または1D）組み合わせを使用して欠損を補完
}

/**
 * ファセット定義
 */
export type FacetDef = FacetWrapDef | FacetGridDef;

// =============================================================================
// UI State
// =============================================================================

/**
 * UI状態（折りたたみセクションの開閉状態など）
 */
export interface CustomGraphUIState {
  // 閉じているセクションのID配列
  collapsedSections?: string[];
}

// =============================================================================
// Custom Graph Config
// =============================================================================

/**
 * Custom Graph設定
 */
export interface CustomGraphConfig extends BaseGraphConfig {
  type: 'custom';

  // グローバルなaesthetic mapping
  globalAes?: AesMapping;

  // レイヤー配列
  layers: LayerDef[];

  // Scales設定
  scales?: ScalesDef;

  // 座標系設定
  coord?: CoordDef;

  // ファセット設定
  facets?: FacetDef;

  // ズーム設定
  zoom?: {
    enabled: boolean;
  };

  // UI状態（折りたたみセクションの開閉状態など）
  uiState?: CustomGraphUIState;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * 型ガード: ContinuousScaleDefかどうかを判定
 */
export function isContinuousScale(scale: AxisScaleDef | undefined): scale is ContinuousScaleDef {
  if (!scale) return false;
  return !scale.type || scale.type !== 'categorical';
}

/**
 * 型ガード: CategoricalScaleDefかどうかを判定
 */
export function isCategoricalScale(scale: AxisScaleDef | undefined): scale is CategoricalScaleDef {
  if (!scale) return false;
  return scale.type === 'categorical';
}

/**
 * 自然順ソート（Natural Sort）の比較関数
 * 文字列内の数値を数値として認識してソートする
 *
 * 例:
 * - 通常のソート: "1", "10", "11", "12", "2", "3", ...
 * - 自然順ソート: "1", "2", "3", ..., "10", "11", "12", ...
 *
 * @param a 比較する文字列A
 * @param b 比較する文字列B
 * @returns -1 (a < b), 0 (a === b), 1 (a > b)
 */
export function naturalSort(a: string, b: string): number {
  // 数値と非数値に分割するための正規表現
  const splitRegex = /(\d+)/g;

  const aParts = a.split(splitRegex);
  const bParts = b.split(splitRegex);

  const maxLength = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || '';
    const bPart = bParts[i] || '';

    // 両方が数値の場合は数値として比較
    const aNum = parseFloat(aPart);
    const bNum = parseFloat(bPart);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) {
        return aNum - bNum;
      }
    } else {
      // 文字列として比較
      if (aPart !== bPart) {
        return aPart.localeCompare(bPart);
      }
    }
  }

  return 0;
}

/**
 * カテゴリカルスケールのドメイン（カテゴリの順序）を決定
 *
 * 優先順位:
 * 1. CategoricalScaleDef.limitsが指定されている場合はそれを使用
 * 2. 未指定の場合は自然順ソートを適用
 *
 * @param categories データから抽出されたカテゴリの配列
 * @param scaleDef カテゴリカルスケール設定（オプション）
 * @returns ソート済みカテゴリ配列
 */
export function getCategoricalDomain(
  categories: string[],
  scaleDef?: CategoricalScaleDef
): string[] {
  // limitsが指定されている場合はそれを使用
  if (scaleDef?.limits && scaleDef.limits.length > 0) {
    return scaleDef.limits;
  }

  // breaksが指定されている場合はフィルタリング
  let filteredCategories = categories;
  if (scaleDef?.breaks && scaleDef.breaks.length > 0) {
    const breaksSet = new Set(scaleDef.breaks);
    filteredCategories = categories.filter(cat => breaksSet.has(cat));
  }

  // 自然順ソートを適用
  return [...filteredCategories].sort(naturalSort);
}

/**
 * デフォルトのCustomGraphConfig生成
 */
export function createDefaultCustomGraphConfig(datasetId: DatasetId): CustomGraphConfig {
  return {
    type: 'custom',
    datasetId,
    layers: [],
  };
}

/**
 * デフォルトのLayer生成（散布図用）
 */
export function createDefaultScatterLayer(): LayerDef {
  return {
    name: 'Points',
    geom: {
      type: 'point',
      defaults: {
        size: 5,
        alpha: 0.7,
      },
    } as PointGeomDef,
    stat: { type: 'identity' } as IdentityStatDef,
    position: { type: 'identity' } as IdentityPositionDef,
    visible: true,
  };
}

/**
 * デフォルトのLayer生成（折れ線グラフ用）
 */
export function createDefaultLineLayer(): LayerDef {
  return {
    name: 'Line',
    geom: {
      type: 'line',
      defaults: {
        strokeWidth: 2,
        lineType: 'solid',
        alpha: 0.9,
      },
    } as LineGeomDef,
    stat: { type: 'identity' } as IdentityStatDef,
    position: { type: 'identity' } as IdentityPositionDef,
    visible: true,
  };
}

/**
 * デフォルトのLayer生成（棒グラフ用）
 */
export function createDefaultBarLayer(): LayerDef {
  return {
    name: 'Bar',
    geom: {
      type: 'bar',
      defaults: {
        width: 0.8,
        alpha: 0.8,
      },
    } as BarGeomDef,
    stat: { type: 'identity' } as IdentityStatDef,
    position: { type: 'stack' } as StackPositionDef,
    visible: true,
  };
}

/**
 * デフォルトのLayer生成（面グラフ用）
 */
export function createDefaultAreaLayer(): LayerDef {
  return {
    name: 'Area',
    geom: {
      type: 'area',
      defaults: {
        alpha: 0.5,
      },
    } as AreaGeomDef,
    stat: { type: 'identity' } as IdentityStatDef,
    position: { type: 'identity' } as IdentityPositionDef,
    visible: true,
  };
}

/**
 * デフォルトのLayer生成（タイル/ヒートマップ用）
 */
export function createDefaultTileLayer(): LayerDef {
  return {
    name: 'Tile',
    geom: {
      type: 'tile',
      defaults: {
        alpha: 1.0,
        stroke: '#ffffff',
        strokeWidth: 0.5,
      },
    } as TileGeomDef,
    stat: { type: 'identity' } as IdentityStatDef,
    position: { type: 'identity' } as IdentityPositionDef,
    visible: true,
  };
}
